import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Modal, Table, Badge, Alert } from 'react-bootstrap';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';

function P0SyncGapsModal({ show, onHide, gaps, selectedDate, onCreateTask, loading }) {
  const [creatingTasks, setCreatingTasks] = useState(new Set());

  const handleCreateTask = (gap, index) => {
    setCreatingTasks(prev => new Set([...prev, index]));
    onCreateTask(gap).finally(() => {
      setCreatingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    });
  };

  const formatTime = dateStr => {
    try {
      const date = new Date(dateStr);
      return format(date, 'HH:mm:ss');
    } catch (e) {
      return dateStr;
    }
  };

  const formatDuration = minutes => {
    if (minutes < 60) {
      return `${minutes.toFixed(1)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes.toFixed(0)}m`;
  };

  return (
    <Modal animation={false} centered show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>P0 Sync Execution Gaps - {format(selectedDate, 'yyyy-MM-dd')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Analyzing P0 sync gaps...</p>
          </div>
        ) : (
          <>
            {gaps.length === 0 ? (
              <Alert variant="success">
                <h5>✅ No gaps found!</h5>
                <p>
                  All time periods on {format(selectedDate, 'yyyy-MM-dd')} appear to be covered by
                  P0 sync tasks.
                </p>
              </Alert>
            ) : (
              <>
                <Alert variant="info">
                  <h6>
                    <FontAwesomeIcon icon={faChartBar} className="mr-1" />
                    Analysis Results
                  </h6>
                  <p className="mb-1">
                    <strong>Date analyzed:</strong> {format(selectedDate, 'yyyy-MM-dd')}
                  </p>
                  <p className="mb-1">
                    <strong>Gaps found:</strong> {gaps.length}
                  </p>
                  <p className="mb-0">
                    <strong>Total uncovered time:</strong>{' '}
                    {formatDuration(gaps.reduce((sum, gap) => sum + gap.duration_minutes, 0))}
                  </p>
                </Alert>

                <Table striped bordered hover className="mt-3">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.map((gap, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{formatTime(gap.start)}</td>
                        <td>{formatTime(gap.end)}</td>
                        <td>
                          <Badge variant="warning">{formatDuration(gap.duration_minutes)}</Badge>
                        </td>
                        <td>
                          <Badge variant="danger">Missing Coverage</Badge>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleCreateTask(gap, index)}
                            disabled={creatingTasks.has(index)}
                          >
                            {creatingTasks.has(index) ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm mr-1"
                                  role="status"
                                />
                                Creating...
                              </>
                            ) : (
                              'Create Task for this Period'
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Alert variant="warning" className="mt-3">
                  <h6>Important Notes:</h6>
                  <ul className="mb-0">
                    <li>
                      Creating tasks will schedule P0 sync jobs to cover these missed time periods
                    </li>
                    <li>Tasks will be created with high priority (PRIORITY_1)</li>
                    <li>Each task will process data for its specific time window</li>
                    <li>
                      You can create tasks individually or close this dialog to create them later
                    </li>
                  </ul>
                </Alert>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        {gaps.length > 0 && (
          <Button
            variant="success"
            onClick={() => {
              gaps.forEach((gap, index) => handleCreateTask(gap, index));
            }}
            disabled={true}
          >
            {creatingTasks.size > 0 ? 'Creating Tasks...' : 'Create All Tasks'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

P0SyncGapsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  gaps: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
      duration_minutes: PropTypes.number.isRequired,
    }),
  ).isRequired,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  onCreateTask: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

P0SyncGapsModal.defaultProps = {
  loading: false,
};

export default P0SyncGapsModal;
